import { DialogDescription } from "@radix-ui/react-dialog";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useInventoryContext } from "../../../hooks/use-inventory";
import { useSubscriptionTooling } from "../../../hooks/use-subscription";
import { CollectionsEnum } from "../../../lib/enums";
import { InventoryItem } from "../../../lib/types";

import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogContent,
  DialogTitle,
  DialogClose,
  DialogTrigger,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectGroup,
  SelectItem,
  SelectLabel,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "../../../components/ui";

import { toast } from "sonner"
import { CategoryDialog } from "../categories/category-dialog";
import { Link } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { BarcodeItem } from "../../../components/molecules/barcode-item";
import {
  Package,
  DollarSign,
  Hash,
  MapPin,
  Tag,
  Barcode as BarcodeIcon,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle
} from "lucide-react";


type InventoryDialogProps = {
  isShowing: boolean,
  editItemId?: string,
  close: () => void
}

export const InventoryDialog: FC<InventoryDialogProps> = ({
  isShowing,
  editItemId,
  close
}) => {
  const { getValues, setValue, register, formState, reset, watch } = useForm()
  const {
    units,
    bins,
    unitNameMap,
    binLocationMap,
    categoryMap,
    inventory,
    updateInventoryElem,
    addInventoryElem
  } = useInventoryContext()

  // Watch form values for reactive updates
  const categoryValue = watch("category")
  const unitValue = watch("unit")

  const {
    canAddInventoryItem,
    getSubscriptionLimits,
    hasBarcodeAccess
  } = useSubscriptionTooling()

  const [showAddCategory, setShowAddCategory] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState("")
  const [skuValue, setSkuValue] = useState("")
  const [currentStep, setCurrentStep] = useState(1)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const subscriptionLimits = getSubscriptionLimits()
  const inventoryCount = inventory.length
  const canAddMore = canAddInventoryItem(inventoryCount)

  const barcodeRef = useRef(null)

  const steps = [
    { number: 1, title: "Basic Info", icon: Package },
    { number: 2, title: "Pricing & Quantity", icon: DollarSign },
    { number: 3, title: "Location", icon: MapPin },
    { number: 4, title: "Review", icon: CheckCircle2 }
  ]

  useEffect(() => {
    if (editItemId) {
      const editItem = inventory.find((i) => i.id === editItemId)

      if (editItem) {
        reset({
          name: editItem.name,
          category: editItem.category,
          unit: `${editItem.unit}`,
          value: editItem.value,
          location: editItem?.location,
          quantity: editItem?.quantity || 1,
          sku: editItem?.sku || ""
        })

        setSelectedUnit(editItem.unit)
        setSkuValue(editItem?.sku)
        setCurrentStep(4) // Go to review for edits

      } else {
        toast.error("error trying to edit")
      }
    } else {
      reset({
        name: "",
        category: "",
        unit: "",
        value: 0,
        location: "",
        quantity: 1,
        sku: "",
      })
      setCurrentStep(1)
    }
  }, [editItemId, isShowing])

  useEffect(() => {
    if (false) {
      try {
        JsBarcode(barcodeRef.current, skuValue, {
          format: "CODE128",
          width: 2,
          height: 100,
          displayValue: true,
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [skuValue, barcodeRef.current])

  const binSelectOptions = useMemo(() => {
    // Only clear location if the unit actually changed and current location is not valid for new unit
    const currentLocation = getValues("location")
    const validBins = bins.filter((i) => i.unit === unitValue)
    const isCurrentLocationValid = validBins.some((bin) => bin.id === currentLocation)

    if (!isCurrentLocationValid && currentLocation) {
      setValue("location", "")
    }

    return validBins.map((k) => {
      return <SelectItem value={k.id} key={k.id}>{binLocationMap[k.id]}</SelectItem>
    })
  }, [unitValue, bins, binLocationMap, getValues, setValue])

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 1:
        if (!getValues("name")) newErrors.name = "Item name is required"
        if (!getValues("category")) newErrors.category = "Category is required"
        break
      case 2:
        if (!getValues("value") || Number(getValues("value")) < 0) newErrors.value = "Valid value is required"
        if (!getValues("quantity") || Number(getValues("quantity")) < 1) newErrors.quantity = "Quantity must be at least 1"
        break
      case 3:
        if (!getValues("unit")) newErrors.unit = "Unit is required"
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSave = () => {
    // Check subscription limit when adding new items (not editing)
    if (!editItemId && !canAddMore) {
      toast.error(`You've reached your plan limit of ${subscriptionLimits.maxInventoryItems} items. Upgrade to add more!`)
      return
    }

    const inventoryItem = {
      name: getValues("name"),
      unit: getValues("unit"),
      category: getValues("category"),
      location: getValues("location") || "",
      value: Number(getValues("value")),
      quantity: Number(getValues("quantity")),
      sku: getValues("sku") || "",
    } as InventoryItem

    try {
      if (editItemId) {
        inventoryItem.id = editItemId
        updateInventoryElem(inventoryItem, CollectionsEnum.INVENTORY_ITEM)
        toast.success(`${inventoryItem.name} updated successfully!`)
      } else {
        addInventoryElem(inventoryItem, CollectionsEnum.INVENTORY_ITEM)
        toast.success(`${inventoryItem.name} added to inventory!`)
      }
    } catch (e) {
      console.error(e)
      toast.error("Failed to save item")
    }
    close()
  }

  return (
    <>
      <Dialog
        open={isShowing}
        onOpenChange={close}
      >
        <DialogTrigger />
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-neutral800 dark:border-neutral700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-neutral100">
              <Package className="text-sumoPrimary" width={24} height={24} />
              {editItemId ? "Edit" : "Add"} Inventory Item
            </DialogTitle>
          </DialogHeader>

          {/* Subscription Limit Warning */}
          {!editItemId && (
            <div className={`rounded-lg p-3 ${!canAddMore
              ? 'bg-error50 dark:bg-error900/20 border border-error500 dark:border-error600'
              : inventoryCount >= subscriptionLimits.maxInventoryItems * 0.8
                ? 'bg-warning50 dark:bg-warning900/20 border border-warning500 dark:border-warning600'
                : 'bg-neutral100 dark:bg-neutral700 border border-neutral300 dark:border-neutral600'
              }`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Package
                    width={16}
                    height={16}
                    className={
                      !canAddMore
                        ? 'text-error600 dark:text-error400'
                        : inventoryCount >= subscriptionLimits.maxInventoryItems * 0.8
                          ? 'text-warning600 dark:text-warning400'
                          : 'text-neutral600 dark:text-neutral400'
                    }
                  />
                  <span className={`text-sm font-medium ${!canAddMore
                    ? 'text-error900 dark:text-error200'
                    : inventoryCount >= subscriptionLimits.maxInventoryItems * 0.8
                      ? 'text-warning900 dark:text-warning200'
                      : 'text-neutral700 dark:text-neutral300'
                    }`}>
                    Inventory Usage: {inventoryCount} / {subscriptionLimits.maxInventoryItems === Infinity ? '∞' : subscriptionLimits.maxInventoryItems}
                  </span>
                </div>
                {!canAddMore && (
                  <span className="text-xs text-error700 dark:text-error300 font-semibold">Limit Reached</span>
                )}
              </div>
            </div>
          )}

          {/* Step Progress Indicator */}
          <div className="py-4 px-2">
            <div className="flex items-start justify-between">
              {steps.map((step, index) => {
                const StepIcon = step.icon
                const isActive = currentStep === step.number
                const isCompleted = currentStep > step.number
                const isClickable = isCompleted || isActive

                return (
                  <>
                    <div key={step.number} className="flex flex-col items-center">
                      <div
                        onClick={() => isClickable && setCurrentStep(step.number)}
                        className={`
                          w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0
                          ${isActive ? 'bg-sumoPrimary text-sumoNavy shadow-yellow ring-4 ring-sumoPrimary/20' : ''}
                          ${isCompleted ? 'bg-success500 text-white hover:bg-success600 cursor-pointer' : ''}
                          ${!isActive && !isCompleted ? 'bg-neutral200 dark:bg-neutral700 text-neutral500 dark:text-neutral400' : ''}
                          ${isClickable && !isActive ? 'hover:scale-110' : ''}
                        `}
                      >
                        {isCompleted ? <CheckCircle2 width={20} height={20} /> : <StepIcon width={20} height={20} />}
                      </div>
                      <p className={`text-[10px] sm:text-xs mt-1.5 font-medium text-center leading-tight max-w-[70px] ${isActive ? 'text-sumoPrimary' : isCompleted ? 'text-success600' : 'text-neutral500 dark:text-neutral400'}`}>
                        {step.title}
                      </p>
                    </div>
                    {/* Show connector line only on larger screens */}
                    {index < steps.length - 1 && (
                      <div className={`hidden sm:block h-0.5 flex-1 mt-6 mx-2 ${isCompleted ? 'bg-success500' : 'bg-neutral200 dark:bg-neutral700'}`} />
                    )}
                  </>
                )
              })}
            </div>
          </div>

          {/* Form Content */}
          <div className="min-h-[300px]">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fade-in">
                {units.length === 0 && (
                  <div className="bg-warning50 dark:bg-warning900/20 border-2 border-warning500 dark:border-warning600 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="text-warning700 dark:text-warning400 flex-shrink-0" width={20} height={20} />
                    <div className="text-sm">
                      <p className="font-semibold text-warning900 dark:text-warning200">No units configured</p>
                      <p className="text-warning700 dark:text-warning300">Go to <Link to="/settings" className="underline font-semibold">Settings</Link> to add your first storage unit</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <Package width={16} height={16} className="text-sumoPrimary" />
                    Item Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Laptop, Office Chair, Screwdriver Set"
                    {...register("name", { required: true, maxLength: 30 })}
                    className={errors.name ? 'border-error500' : ''}
                  />
                  {errors.name && <p className="text-xs text-error500 flex items-center gap-1"><AlertCircle width={12} /> {errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <BarcodeIcon width={16} height={16} className="text-sumoPrimary" />
                    SKU / Barcode
                    <span className="text-neutral400 dark:text-neutral500 font-normal text-xs">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., SKU-12345, UPC-67890"
                    {...register("sku", {
                      onChange: (e) => setSkuValue(e.target.value),
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <Tag width={16} height={16} className="text-sumoPrimary" />
                    Category *
                  </label>
                  <Select
                    value={categoryValue}
                    onValueChange={(value: string) => { setValue("category", value) }}
                  >
                    <SelectTrigger className={errors.category ? 'border-error500' : ''}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Categories</SelectLabel>
                        {Object.keys(categoryMap).map((k) => (
                          <SelectItem value={k} key={k}>{categoryMap[k]}</SelectItem>
                        ))}
                      </SelectGroup>
                      <div className="p-2 border-t border-neutral200 dark:border-neutral700">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-sumoPrimary hover:text-sumoAmber"
                          onClick={() => setShowAddCategory(true)}
                        >
                          + Add New Category
                        </Button>
                      </div>
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-xs text-error500 flex items-center gap-1"><AlertCircle width={12} /> {errors.category}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Pricing & Quantity */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                      <DollarSign width={16} height={16} className="text-sumoPrimary" />
                      Value Per Item *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...register("value", { required: true })}
                      className={errors.value ? 'border-error500' : ''}
                    />
                    {errors.value && <p className="text-xs text-error500 flex items-center gap-1"><AlertCircle width={12} /> {errors.value}</p>}
                    <p className="text-xs text-neutral500 dark:text-neutral400">Estimated value per unit</p>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                      <Hash width={16} height={16} className="text-sumoPrimary" />
                      Quantity *
                    </label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      placeholder="1"
                      {...register("quantity", { required: true })}
                      className={errors.quantity ? 'border-error500' : ''}
                    />
                    {errors.quantity && <p className="text-xs text-error500 flex items-center gap-1"><AlertCircle width={12} /> {errors.quantity}</p>}
                    <p className="text-xs text-neutral500 dark:text-neutral400">Number of units</p>
                  </div>
                </div>

                {/* Total Value Card */}
                {getValues("value") && getValues("quantity") && (
                  <Card className="bg-sumoCream/30 dark:bg-sumoPrimary/10 border-sumoPrimary/30 dark:border-sumoPrimary/40">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-neutral700 dark:text-neutral300">Total Estimated Value</span>
                        <span className="text-2xl font-bold text-sumoPrimary">
                          ${(Number(getValues("value")) * Number(getValues("quantity"))).toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 3: Location */}
            {currentStep === 3 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                    <MapPin width={16} height={16} className="text-sumoPrimary" />
                    Storage Unit *
                  </label>
                  <Select
                    value={unitValue}
                    onValueChange={(value: string) => {
                      setValue("unit", value)
                      setSelectedUnit(value)
                    }}
                  >
                    <SelectTrigger className={errors.unit ? 'border-error500' : ''}>
                      <SelectValue placeholder="Select storage unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Units</SelectLabel>
                        {Object.keys(unitNameMap).map((k) => (
                          <SelectItem value={k} key={k}>{unitNameMap[k]}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {errors.unit && <p className="text-xs text-error500 flex items-center gap-1"><AlertCircle width={12} /> {errors.unit}</p>}
                </div>

                {unitValue && (
                  <div className="space-y-2 animate-fade-in">
                    <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
                      <Package width={16} height={16} className="text-sumoTeal" />
                      Bin / Location
                      <span className="text-neutral400 dark:text-neutral500 font-normal text-xs">(Optional)</span>
                    </label>
                    <Select
                      value={getValues("location") || undefined}
                      onValueChange={(value: string) => { setValue("location", value) }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select bin location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Bins in {unitNameMap[unitValue]}</SelectLabel>
                          {binSelectOptions.length > 0 ? binSelectOptions.map((i) => i) : (
                            <div className="p-2 text-sm text-neutral500">No bins available</div>
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-neutral500 dark:text-neutral400">Specify exact bin or shelf location within unit</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-4 animate-fade-in">
                <Card className="dark:bg-neutral800 dark:border-neutral700">
                  <CardHeader>
                    <CardTitle className="text-lg dark:text-neutral100">Review Item Details</CardTitle>
                    <CardDescription className="dark:text-neutral400">Please confirm the information before saving</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Item Name</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">{getValues("name") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">SKU</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">{getValues("sku") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Category</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">{categoryMap[getValues("category")] || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Unit Value</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">${Number(getValues("value") || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Quantity</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">{getValues("quantity") || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Total Value</p>
                        <p className="text-lg font-bold text-sumoPrimary">
                          ${(Number(getValues("value") || 0) * Number(getValues("quantity") || 0)).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Storage Unit</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">{unitNameMap[getValues("unit")] || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase">Bin Location</p>
                        <p className="text-lg font-semibold text-neutral900 dark:text-neutral100">
                          {getValues("location") ? binLocationMap[getValues("location")] : "—"}
                        </p>
                      </div>
                    </div>

                    {getValues("sku") && (
                      <div className="pt-4 border-t border-neutral200 dark:border-neutral700">
                        <p className="text-xs font-semibold text-neutral500 dark:text-neutral400 uppercase mb-2">Barcode Preview</p>
                        {hasBarcodeAccess() ? (
                          <BarcodeItem sku={getValues("sku")} />
                        ) : (
                          <div className="bg-gradient-to-r from-sumoPrimary/10 to-sumoAmber/10 dark:from-sumoPrimary/20 dark:to-sumoAmber/20 border-2 border-sumoPrimary/30 dark:border-sumoPrimary/50 rounded-lg p-6 text-center">
                            <BarcodeIcon className="mx-auto text-sumoPrimary mb-3" width={48} height={48} />
                            <p className="font-bold text-neutral900 dark:text-neutral100 mb-2">Barcode Generation</p>
                            <p className="text-sm text-neutral600 dark:text-neutral400 mb-3">
                              Upgrade to Premium to generate and print barcodes for your inventory items
                            </p>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => window.location.href = '/#pricing'}
                            >
                              Upgrade to Premium
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Navigation Footer */}
          <DialogFooter className="flex justify-between gap-2 mt-6">
            <div className="flex gap-2 flex-1">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft width={16} height={16} />
                  Previous
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <DialogClose asChild>
                <Button variant="outline">
                  Cancel
                </Button>
              </DialogClose>

              {currentStep < 4 ? (
                <Button
                  variant="primary"
                  onClick={nextStep}
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight width={16} height={16} />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSave}
                  className="flex items-center gap-2"
                >
                  <CheckCircle2 width={16} height={16} />
                  {editItemId ? "Update" : "Add"} Item
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoryDialog
        isShowing={showAddCategory}
        close={() => { setShowAddCategory(false) }}
      />
    </>
  )
}
